import React, { useContext, ReactNode, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { QuizSession } from 'graphql/fragments/types/QuizSession';
import { CreateUserQuizSession, CreateUserQuizSessionVariables } from 'graphql/mutations/types/CreateUserQuizSession';
import { UpdateUserQuizSession, UpdateUserQuizSessionVariables } from 'graphql/mutations/types/UpdateUserQuizSession';
import { createUserQuizSession, updateUserQuizSession } from 'graphql/mutations';
import { useUserContext } from './user-context';
import { useNavigate } from 'react-router-dom';
import { NavUtils } from 'utils';
import { useEventContext } from './event-context';
import { UserQuizSession } from 'graphql/fragments/types/UserQuizSession';
import { getQuizUserSession } from 'graphql/queries';
import {
    UserQuizSessionBySessionAndUser,
    UserQuizSessionBySessionAndUserVariables
} from 'graphql/queries/types/UserQuizSessionBySessionAndUser';

interface QuizSessionContextType {
    session: QuizSession;
    userSession: UserQuizSession;
    updateUserSession: (data: UpdateUserQuizSessionVariables) => Promise<UserQuizSession>;
}

const QuizSessionContext = React.createContext<QuizSessionContextType | undefined>(undefined);

export function useQuizSessionContext() {
    return useContext(QuizSessionContext);
}

export function QuizSessionProvider({ quizSession, children }: { quizSession: QuizSession; children?: ReactNode | undefined }) {
    const user = useUserContext();
    const event = useEventContext();
    const navigate = useNavigate();

    const [userSession, setUserSession] = useState<UserQuizSession>();

    const [getUserSession] = useLazyQuery<UserQuizSessionBySessionAndUser, UserQuizSessionBySessionAndUserVariables>(getQuizUserSession);
    const [createUserSessionMutation] = useMutation<CreateUserQuizSession, CreateUserQuizSessionVariables>(createUserQuizSession);
    const [updateUserSessionMutation] = useMutation<UpdateUserQuizSession, UpdateUserQuizSessionVariables>(updateUserQuizSession);

    useEffect(() => {
        const createSession = async () => {
            const startTime = new Date().toISOString();
            const fetchedData = await getUserSession({
                variables: {
                    sessionId: quizSession.id,
                    userId: user.id
                }
            });

            if (fetchedData.data?.quiz_master_quiz_session_user_progress.length === 1) {
                setUserSession(fetchedData.data!.quiz_master_quiz_session_user_progress[0]);
                return;
            }

            try {
                const returnData = await createUserSessionMutation({
                    variables: {
                        sessionId: quizSession.id,
                        timeStarted: startTime,
                        userId: user.id
                    }
                });

                if (returnData.errors || !returnData.data) {
                    console.error('Error creating session', returnData.errors);
                    navigate(NavUtils.getQuizSelectorPath(event?.alias));
                }

                setUserSession({
                    id: returnData.data!.insert_quiz_master_quiz_session_user_progress_one?.id!,
                    correctAnswers: 0,
                    answersAttempted: 0,
                    sessionId: quizSession.id,
                    timeStarted: startTime,
                    userId: user.id,
                    additionalInfo: undefined,
                    timeLastAnswer: undefined,
                    hasEnded: false
                });
            } catch (error) {
                console.error('Error creating session', error);
                navigate(NavUtils.getQuizSelectorPath(event?.alias));
            }
        };

        createSession();
    }, [createUserSessionMutation, event?.alias, getUserSession, navigate, quizSession.id, user.id]);

    const updateUserSession = async (data: UpdateUserQuizSessionVariables): Promise<UserQuizSession> => {
        console.log("executing mutation")
        const returnData = await updateUserSessionMutation({
            variables: data
        });

        if (returnData.errors || !returnData.data) {
            console.error('Error updating session', returnData.errors);
            return userSession!;
        }

        const updatedUserSession: UserQuizSession = {
            ...userSession!,
            answersAttempted: data.answersAttempted!,
            correctAnswers: data.correctAnswers!,
            timeLastAnswer: data.currentTime,
            additionalInfo: data.additionalInfo
        };
        setUserSession(updatedUserSession);

        return updatedUserSession;
    };

    const contextValue: QuizSessionContextType | undefined = quizSession
        ? {
              session: quizSession,
              userSession: userSession!,
              updateUserSession
          }
        : undefined;

    return <QuizSessionContext.Provider value={contextValue}>{children}</QuizSessionContext.Provider>;
}
